<template>
  <in-view
    :threshold="thresholdHeading"
    :threshold-type="'px'"
    class="slide-into-view-transition"
  >
    <section
      class="search-box"
    >
      <slot />
      <label
        for="search-box"
        class="search-box__label"
        aria-describedby="no-result-found"
      >
        <input
          id="search-box"
          v-model="searchQuery"
          class="search-box__input"
          type="search"
          autocapitalize="off"
          autocomplete="off"
          :placeholder="searchText"
          @keyup.enter="onEnter"
        >
        <svgicon
          v-if="!isLoading"
          name="Navigation/Search"
          class="search-box-icon search-box-icon--search"
          :original="true"
        />
        <svgicon
          v-else
          name="Misc/Loading-red-animated"
          class="loading search-box-icon search-box-icon--search"
        />
        <svgicon
          v-if="searchQuery != ''"
          name="Controls/Close"
          class="search-box-icon search-box-icon--close"
          :original="true"
          @click="clearSearch"
        />
      </label>

      <searchNotification
        v-if="isErrorMessage"
        :is-error-message="isErrorMessage"
      />

      <div
        v-if="onEnterPress"
        role="status"
        aria-live="polite"
        class="visually-hidden"
      >
        <template v-if="showAccessiblityGuideOnResult">
          Din sökning för {{ searchQuery }} gav
          {{ searchResult.length }} resultat. Tabba för att se
          {{ searchResult.length > 1 ? "resultatet" : "resultaten" }}
        </template>
        <template v-else-if="isErrorMessage">
          Oops, tekniskt fel
          Vi upplever just nu tekniska problem. Vänligen testa att söka igen!
        </template>
        <template v-else>
          Din sökning för {{ searchQuery }} gav inget resultat. Kontrollera att
          orden är rättstavade eller försök med andra sökord.
        </template>
      </div>

      <div
        v-if="noResultFound"
        class="search-box__no-results font-rubrik-1--light"
      >
        <span class="search-box__no-results-enhance">Inga resultat.</span> Testa att ändra din sökning eller ta bort ett filter.
      </div>

      <div class="search-box__stats">
        <template
          v-for="(stat, i) in searchStats"
        >
          <a
            :key="stat.id + 'a'"
            class="search-box__stats-link font-rubrik-1--light"
            :href="'#' + stat.id"
          >{{ stat.name }}</a>
          <span
            :key="stat.id + 'span'"
            class="search-box__stats-link-extra font-rubrik-1--light"
          >({{ stat.totalHits }})</span><span
            v-if="i + 1 < searchStats.length"
            :key="stat.id + 'comma'"
            class="font-rubrik-1--light"
          >, </span>
        </template>
      </div>
    </section>
  </in-view>
</template>

<script>
import axios from 'axios'
import searchNotification from "@/vue/components/common/searchNotification"
import dataLayer from '@/analytics/dataLayer'

import debounce from "lodash.debounce"
import "#/Navigation/Search"
import "#/Controls/Close"

export default {
  name: "SearchBox",
  components: {
    searchNotification
  },
  props: {
    searchText: {
      type: String,
      default: "Sök"
    },
    apiUrl: {
      type: String,
      default: ""
    },
    limit: {
      type: Object,
      default () {
        return {
          sm: 3,
          md: 3,
          lg: 3
        }
      }
    },
    trackingTitle: {
      type: String,
      default: ""
    },
  },
  data () {
    return {
      searchQuery: "",
      searchResult: [],
      searchStats: [],
      hasSearched: false,
      onEnterPress: false,
      isErrorMessage: false,
      noResultFound: false,
      isLoading: false,
      translationMap: {
        articles: "Artiklar",
        recipes: "Recept",
        recipeCollections: "Receptsamlingar"
      }
    }
  },
  computed: {
    lowerCaseQuery () {
      return this.searchQuery.trim().toLowerCase()
    },
    searchLimit () {
      if (this.$root.isSmallViewPort) {
        return this.limit.sm
      } else if (this.$root.isMediumViewPort) {
        return this.limit.md
      } else {
        return this.limit.lg
      }
    },
    showAccessiblityGuideOnResult () {
      return (
        this.hasSearched &&
        !this.noResultFound &&
        this.lowerCaseQuery.length >= 3 &&
        this.onEnterPress
      )
    },
    thresholdHeading () {
      return this.$root.isLargeViewport ? 32: 28
    }
  },
  watch: {
    searchQuery () {
      this.doSearch()
      this.onEnterPress = false
    }
  },
  methods: {
    doSearch: debounce(async function () {
      // Reset
      this.isErrorMessage = false
      this.noResultFound = false
      this.hasSearched = false

      if (this.lowerCaseQuery.length >= 3) {
        try {
          this.isLoading = true
          const encodedSearchQuery = encodeURIComponent(this.lowerCaseQuery)
          const response = await axios.get(`${this.$root.envConfig.baseUrl}${this.apiUrl}?query=${encodedSearchQuery}&take=${this.searchLimit}`)
          const data = response.data
          this.isLoading = false

          const hasResults = data.results && Object.entries(data.results).length > 0
          this.hasSearched = true
          this.handleResultCount(data.stats)

          if (hasResults) {
            this.searchResult = data
            this.$emit("searchResult", data)
          } else {
            this.noResultFound = true
            this.searchStats = []
            this.$emit("searchResult", null)
          }
          this.trackSearch(this.searchResult)
        } catch (err){
            console.log("error:", err)
            this.isLoading = false
            this.isErrorMessage = true
            this.hasSearched = false
            this.trackSearch(this.searchResult)
        }
      } else {
        this.clearSearchResult()
      }
    }, 500),
    handleResultCount (stats) {
      this.searchStats = []

      Object.keys(stats).forEach(list => {
        if (stats[list].totalHits > 0) {
          this.searchStats.push({
            name: this.translationMap[list],
            id: list,
            totalHits: stats[list].totalHits
          })
        }
      })
    },
    clearSearch () {
      this.searchQuery = ""
      this.clearSearchResult()
    },
    clearSearchResult () {
      this.searchResult = []
      this.hasSearched = false
    },
    onEnter () {
      this.onEnterPress = true
    },
    trackSearch () {
      dataLayer.add({
        event: 'search',
        succeeded: (!this.isErrorMessage && !this.noResultFound) ? "1" : "0",
        searchTerm: this.searchQuery,
        searchCategory: this.trackingTitle,
      })
    },
  }
}
</script>
<style lang="scss">
.search-box {
  &.withBackground {
    background-color: $color-white;
    padding: 1rem;
    border-radius: 0.25rem;

    @include media-breakpoint-up(md) {
      padding: 1.5rem;
    }
  }

  margin-top: 3rem;
  position: relative;

  &__label {
    position: relative;
    width: 100%;
    display: block;
  }

  &__input {
    width: 100%;
    border: none;
    border-radius: 0.25rem;
    appearance: none;
    box-shadow: $box-shadow-primary;
    background-color: $color-white;
    padding: 1rem 3rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: $color-grey-dark;
    fill: $color-grey-dark;

    @include font(text, 3, "normal");

    &::-ms-clear,
    &::-ms-reveal {
      display: none;
      width: 0;
      height: 0;
    }

    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      display: none;
    }

    &::placeholder {
      font-size: 1rem;
    }

    &:focus {
      border: 1px solid #bdbbb9;
      border-bottom: 2px solid $color-red;
    }
  }

  &__stats {
    margin-top: 1rem;

    &-link {
      color: $color-brown-dark;
      border-bottom: 1px solid $color-brown-dark;
    }

    &-link-extra {
      color: $color-brown-dark;
    }
  }

  &__no-results {
    margin-top: 1rem;

    &-enhance {
      color: $color-red;
    }
  }

  &__button-wrapper {
    text-align: center;
    margin-top: 1.5rem;
  }

  .loading-wrapper {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, 50%);

    &__icon-loading {
      width: 40px;
      height: 40px;
      fill: $color-red;
    }
  }

  .button--text-icon {
    .circle-icon {
      align-items: center;
      background: rgba(160, 41, 113, 0.1);
      border-radius: 1.5rem;
      display: inline-flex;
      flex-shrink: 0;
      height: 1.5rem;
      justify-content: center;
      vertical-align: middle;
      width: 1.5rem;
      margin-left: 0.5rem;

      svg {
        margin: 0;
      }
    }
  }

  .search-box-icon {
    position: absolute;
    top: 50%;
    width: 1.5rem;
    height: 1.5rem;
    transform: translateY(-50%);
    fill: $color-grey-dark;

    &--search {
      left: 1rem;
    }

    &--close {
      right: 1rem;
      cursor: pointer;
    }
  }

  .no-result {
    margin-top: 1rem;
  }
}
</style>
